import {
  removeCommaSeparatedValue,
  set,
  toggleCommaSeparatedValue,
} from "@prismicio/editor-support/URLSearchParams"
import { type ReactNode, useEffect, useState, useTransition } from "react"
import { useSearchParams } from "react-router-dom"
import * as z from "zod"

import {
  parseRouteParams,
  useRouteParams,
  type UseRouteParamsArgs,
} from "../app/routing"
import { mainSpaceMeta } from "../app/services/role"
import { DocumentListSearchParamsContext } from "./useDocumentListSearchParams"

interface Props {
  children: ReactNode
}

interface UseOptimisticSearchParamsProps<
  PathShape extends z.ZodRawShape,
  SearchShape extends z.ZodRawShape,
> {
  routeParamsSchema: UseRouteParamsArgs<PathShape, SearchShape>
}

function useOptimisticSearchParams<
  PathShape extends z.ZodRawShape,
  SearchShape extends z.ZodRawShape,
>(props: UseOptimisticSearchParamsProps<PathShape, SearchShape>) {
  const { routeParamsSchema } = props

  const { search: searchParams, setSearch: setSearchParams } =
    useRouteParams(routeParamsSchema)

  const [optimisticSearch, setOptimisticSearchParams] =
    useState<URLSearchParams>(new URLSearchParams(searchParams))

  const [reactRouterSearchParams] = useSearchParams()
  // update optimistic search params on browser navigation (e.g. back button)
  useEffect(() => {
    setOptimisticSearchParams(reactRouterSearchParams)
  }, [reactRouterSearchParams])

  const validatedOptimisticSearch = parseRouteParams(
    Object.fromEntries(optimisticSearch),
    routeParamsSchema.search,
  )

  const [isLoading, startTransition] = useTransition()

  function setSearch(updater: (prev: URLSearchParams) => URLSearchParams) {
    const newValues = updater(optimisticSearch)

    if (newValues.toString() === optimisticSearch.toString()) return

    setOptimisticSearchParams(newValues)
    startTransition(() => {
      setSearchParams(newValues)
    })
  }

  return {
    searchParams,
    search: validatedOptimisticSearch,
    setSearch,
    isLoading,
  }
}

function useDocumentListSearchParamsContext() {
  const { search, setSearch, isLoading, searchParams } =
    useOptimisticSearchParams({
      routeParamsSchema,
    })

  const searchAction = {
    setSearch,
    toggleArrayValue(key: SearchKey, value: string) {
      setSearch((prev) =>
        toggleCommaSeparatedValue(new URLSearchParams(prev), key, value),
      )
    },
    removeArrayValue(key: SearchKey, value: string) {
      setSearch((prev) =>
        removeCommaSeparatedValue(new URLSearchParams(prev), key, value),
      )
    },
    setValue(key: SearchKey, value: string) {
      setSearch((prev) => set(new URLSearchParams(prev), key, value))
    },
  }

  return {
    /* Optimistic search params are updated instantly upon change. */
    optimisticSearchParams: search,
    /* Search params change triggers route transition. These values remain stale until transition is finished. */
    searchParams,
    searchAction,
    isTransitioning: isLoading,
    urlSearchParams: window.location.search,
  }
}

export type DocumentListSearchParamsContextType = ReturnType<
  typeof useDocumentListSearchParamsContext
>

export function DocumentListSearchParamsProvider(props: Props) {
  const { children } = props

  return (
    <DocumentListSearchParamsContext.Provider
      value={useDocumentListSearchParamsContext()}
    >
      {children}
    </DocumentListSearchParamsContext.Provider>
  )
}

const routeParamsSchema = {
  search: {
    term: z.string().transform(intoArray).optional(),
    language: z.string().transform(intoArray).optional(),
    customTypes: z.string().transform(splitCommaSeparatedString).optional(),
    tags: z.string().transform(splitCommaSeparatedString).optional(),
    authors: z.string().transform(splitCommaSeparatedString).optional(),
    status: z.string().transform(splitCommaSeparatedString).optional(),
    group_lang_id: z.string().transform(splitCommaSeparatedString).optional(),
    document_id: z.string().transform(splitCommaSeparatedString).optional(),
    space: z
      .string()
      .optional()
      .transform((value) => value || mainSpaceMeta.id),
  },
}

type SearchKey = keyof typeof routeParamsSchema.search

function intoArray(value: string) {
  return [value]
}

function splitCommaSeparatedString(str: string) {
  if (str === "") return undefined
  return str.split(",")
}
